import { isValidElement } from 'react';
import { capitalize, map, isString } from 'lodash';
import {
  Dialog as MuiDialog, DialogActions, DialogContent, DialogContentText,
  DialogTitle, Typography, IconButton, Grid,
  AppBar, Container,
} from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useIsMobile } from '@client/hooks/dom.hooks';
import { SideDoorIcon } from '@client/images/logos/SideDoorIcon';
import { generateBackdropStyles } from '@client/theme/components/Card';
import { appBar } from '@client/theme/mixins';
import type { ModalState, ModalOptions } from './ModalProps.interface';

const useStyles = makeStyles({ name: 'BaseDialog' })((theme) => ({
  dialogTitle: {
    ...theme.typography.subtitle2 as any,
    padding: theme.spacing(2),

    [theme.breakpoints.up('md')]: {
      paddingLeft: theme.spacing(3),
    },
  },
  dialogTitleTypography: {
    paddingTop: theme.spacing(1),
  },
  dialogTitleCondensed: {
    paddingBottom: 0,
    marginBottom: 0,
  },
  appBar: {
    ...appBar(theme),
    alignItems: 'flex-start',
    justifyContent: 'center',
    borderBottomColor: theme.palette.divider,
    borderBottomStyle: 'solid',
    borderBottomWidth: 1,
    padding: theme.spacing(0, 2),

    [theme.breakpoints.up('md')]: {
      marginBottom: theme.spacing(3),
      padding: 0,
    },
  },
  dialogContent: {
    [theme.breakpoints.up('md')]: {
      padding: theme.spacing(0, 3, 3, 3),
    },

    [theme.breakpoints.down('md')]: {
      justifyContent: 'center',
    },
  },
  dialogContentGutters: {
    padding: 0,
  },
  closeIcon: {
    padding: 0,
    minWidth: 26,
    minHeight: 26,
  },
  sidedoorLogo: {
    padding: 0,
    minWidth: 32,
    minHeight: 32,
  },
  flexContent: {
    display: 'flex',
    flex: 1,
  },
  ...generateBackdropStyles(theme),
}));

interface DialogProps extends Omit<ModalState, 'options'>, ModalOptions {
  readonly handleClose: (event?: unknown) => void;
}

export const Dialog: React.FC<DialogProps> = ({ open, handleClose, ...options }) => {
  const { classes, cx } = useStyles();
  const isMobile = useIsMobile();

  const {
    body, buttons, disableBackdropClick, disableCloseButton,
    disableEscapeKeyDown, fullScreen, fullWidth, maxWidth,
    media, scroll, title, backgroundColor,
    subtitle, disableGutters, DialogTitleProps, dialogTitleCondensed,
    dialogContentComponent: DialogContentComponent = null,
  } = options;

  const showCloseButton = !disableBackdropClick && !disableCloseButton;

  return (
    <MuiDialog
      aria-describedby="alert-dialog-description"
      aria-labelledby="alert-dialog-title"
      classes={{ paper: classes[`color${capitalize(backgroundColor)}`] }}
      disableEscapeKeyDown={disableEscapeKeyDown}
      fullScreen={isMobile || fullScreen}
      fullWidth={fullWidth}
      maxWidth={isMobile ? false : maxWidth}
      open={open}
      scroll={scroll}
      onClose={(event, reason) => {
        if (disableBackdropClick && reason === 'backdropClick') {
          return;
        }

        if (handleClose) {
          handleClose(event);
        }
      }}
    >
      {(title || showCloseButton) && (
        <>
          {fullScreen ? (
            <Container
              maxWidth={maxWidth}
              disableGutters
            >
              <AppBar
                className={classes.appBar}
                position="sticky"
              >
                <Grid
                  container
                  wrap="nowrap"
                  spacing={1.5}
                  alignItems="center"
                >
                  {Boolean(showCloseButton) && (
                    <Grid item>
                      <IconButton
                        aria-label="back"
                        size="medium"
                        onClick={handleClose}
                        edge="start"
                      >
                        <ArrowBackIcon />
                      </IconButton>
                    </Grid>
                  )}

                  <Grid
                    item
                    zeroMinWidth
                    className="spacer"
                  >
                    {isString(title) ? (
                      <Typography variant="body2">
                        {title}
                      </Typography>
                    ) : title}
                  </Grid>
                </Grid>
              </AppBar>
            </Container>
          ) : (
            <DialogTitle
              className={cx(classes.dialogTitle, DialogTitleProps?.className, dialogTitleCondensed && classes.dialogTitleCondensed)}
              {...DialogTitleProps}
            >
              <Grid
                alignItems={title ? 'flex-start' : 'center'}
                justifyContent="space-between"
                spacing={1}
                wrap="nowrap"
                container
              >
                <Grid
                  item
                  zeroMinWidth
                >
                  {title ? (
                    <>
                      {isString(title) ? (
                        <Typography
                          id="alert-dialog-title"
                          variant="subtitle2"
                          component="span"
                          className={classes.dialogTitleTypography}
                          paragraph={Boolean(subtitle)}
                        >
                          {title}
                        </Typography>
                      ) : title}

                      {Boolean(subtitle) && (
                        <Typography
                          variant="body1"
                          component="span"
                        >
                          {subtitle}
                        </Typography>
                      )}
                    </>
                  ) : (
                    <IconButton
                      size="xlarge"
                      className={classes.sidedoorLogo}
                      disabled
                    >
                      <SideDoorIcon />
                    </IconButton>
                  )}
                </Grid>

                {Boolean(showCloseButton) && (
                  <Grid item>
                    <IconButton
                      onClick={handleClose}
                      size="large"
                      className={classes.closeIcon}
                      edge="end"
                    >
                      <CloseIcon />
                    </IconButton>
                  </Grid>
                )}
              </Grid>
            </DialogTitle>
          )}
        </>
      )}

      {isValidElement(media) && (
        <>{media}</>
      )}

      {DialogContentComponent ? <DialogContentComponent>{body}</DialogContentComponent> : (
        <DialogContent className={cx(classes.dialogContent, disableGutters && classes.dialogContentGutters, !buttons.length && classes.flexContent)}>
          {isValidElement(body) ? (
            <>{body}</>
          ) : (
            <DialogContentText
              id="alert-dialog-description"
              variant="body1"
              color="inherit"
            >
              {body}
            </DialogContentText>
          )}
        </DialogContent>
      )}

      {!!buttons.length && (
        <DialogActions>
          <>
            {map(buttons, (button) => button)}
          </>
        </DialogActions>
      )}
    </MuiDialog>
  );
};
